import { useWizardStore } from '@/store/wizardStore'
import type { Lang } from '@/hooks/useLang'

interface Step5PhysicalProps { lang: Lang }

const IP_RATINGS = ['ip44', 'ip54', 'ip65', 'ip67'] as const
type IpRating = typeof IP_RATINGS[number]

const IP_LABELS: Record<IpRating, { fr: string; en: string }> = {
  ip44: { fr: 'IP44 — Projections d\'eau', en: 'IP44 — Water splashes' },
  ip54: { fr: 'IP54 — Poussière + projections', en: 'IP54 — Dust + splashes' },
  ip65: { fr: 'IP65 — Jet d\'eau basse pression', en: 'IP65 — Low-pressure water jet' },
  ip67: { fr: 'IP67 — Immersion temporaire', en: 'IP67 — Temporary immersion' },
}

const COPY = {
  fr: {
    heightLabel: 'Hauteur (cm)',
    heightHint: 'Hauteur totale du robot debout',
    payloadLabel: 'Charge utile (kg)',
    payloadHint: 'Masse maximale portée en continu',
    ipLabel: 'Indice de protection',
    ipHint: 'Résistance à la poussière et à l\'eau',
  },
  en: {
    heightLabel: 'Height (cm)',
    heightHint: 'Total height of robot standing',
    payloadLabel: 'Payload (kg)',
    payloadHint: 'Maximum continuously carried mass',
    ipLabel: 'Protection rating',
    ipHint: 'Dust and water resistance',
  },
}

export default function Step5Physical({ lang }: Step5PhysicalProps) {
  const { draft, update } = useWizardStore()
  const t = COPY[lang]

  const height = draft.step5_height_cm ?? 160
  const payload = draft.step5_payload_kg ?? 10
  const ip = draft.step5_ip_rating ?? 'ip54'

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-3xl">
      {/* Height slider */}
      <div className="glass-card p-8 rounded-xl border border-outline-variant/20">
        <div className="flex items-center gap-3 mb-2">
          <span className="material-symbols-outlined text-primary text-2xl">height</span>
          <span className="font-headline text-base font-bold text-on-surface">{t.heightLabel}</span>
        </div>
        <p className="text-xs text-on-surface-variant mb-6 font-label">{t.heightHint}</p>
        <div className="font-headline text-4xl font-bold text-primary-container mb-4">{height}<span className="text-lg text-on-surface-variant ml-1">cm</span></div>
        <input
          type="range"
          min={120}
          max={210}
          step={5}
          value={height}
          onChange={(e) => update({ step5_height_cm: Number(e.target.value) })}
          className="w-full accent-primary-container"
        />
        <div className="flex justify-between text-xs text-on-surface-variant mt-1 font-label">
          <span>120 cm</span>
          <span>210 cm</span>
        </div>
      </div>

      {/* Payload slider */}
      <div className="glass-card p-8 rounded-xl border border-outline-variant/20">
        <div className="flex items-center gap-3 mb-2">
          <span className="material-symbols-outlined text-secondary text-2xl">fitness_center</span>
          <span className="font-headline text-base font-bold text-on-surface">{t.payloadLabel}</span>
        </div>
        <p className="text-xs text-on-surface-variant mb-6 font-label">{t.payloadHint}</p>
        <div className="font-headline text-4xl font-bold text-secondary mb-4">{payload}<span className="text-lg text-on-surface-variant ml-1">kg</span></div>
        <input
          type="range"
          min={1}
          max={80}
          step={1}
          value={payload}
          onChange={(e) => update({ step5_payload_kg: Number(e.target.value) })}
          className="w-full accent-secondary"
        />
        <div className="flex justify-between text-xs text-on-surface-variant mt-1 font-label">
          <span>1 kg</span>
          <span>80 kg</span>
        </div>
      </div>

      {/* IP rating */}
      <div className="md:col-span-2 glass-card p-8 rounded-xl border border-outline-variant/20">
        <div className="flex items-center gap-3 mb-2">
          <span className="material-symbols-outlined text-primary text-2xl">water_drop</span>
          <span className="font-headline text-base font-bold text-on-surface">{t.ipLabel}</span>
        </div>
        <p className="text-xs text-on-surface-variant mb-6 font-label">{t.ipHint}</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {IP_RATINGS.map((rating) => (
            <button
              key={rating}
              onClick={() => update({ step5_ip_rating: rating })}
              className={`p-4 rounded-lg border text-left transition-all ${ip === rating ? 'border-primary-container/60 bg-primary-container/10 text-primary-container' : 'border-outline-variant/20 text-on-surface-variant hover:border-outline-variant/40'}`}
            >
              <div className="font-headline text-sm font-bold uppercase mb-1">{rating.toUpperCase()}</div>
              <div className="text-xs font-label leading-relaxed">{IP_LABELS[rating][lang]}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
